/**
 * `round CN-B`, `CN-B.1`: **identity's team events, contracted.**
 *
 * `03` WF-CON-26, 28 and 29 name them as postconditions; until this round the
 * doors wrote the row and published nothing, so a birth could not learn that a
 * person had gone absent except by reading her row again. Published in the
 * door's own transaction, as every event of this platform is.
 */
export const IDENTITY_EVENTS = {
  /** WF-CON-26: `{person, state, returnDate}`. */
  staffAvailabilityChanged: "identity.StaffAvailabilityChanged",
  /** WF-CON-28: `{person, at}` , deactivated, never deleted. */
  staffDeactivated: "identity.StaffDeactivated",
  /** WF-CON-29: `{name, role, scope}`. */
  staffInvited: "identity.StaffInvited",
  // Round `L-5`, `M2` (`P0-IDN-033`, `035`, `037`, WF-BNK-25): the bank side of the three, published
  // by the same doors when the organisation is a bank (04 §4.4: « same shape, bank side »).
  analystInvited: "identity.AnalystInvited",
  analystAvailabilityChanged: "identity.AnalystAvailabilityChanged",
  analystDeactivated:
    "identity.AnalystDeactivated" /** WF-CON-29, and every partner's first responsible: the link left, never printed. */,
  invitationSent: "identity.InvitationSent",
  /** WF-CON-35, 04 §4.2.1: `{ invitationId, orgId, orgType, role, invitedBy }`. */
  partnerAdminInvited: "identity.PartnerAdminInvited",
  /**
   * `round CN-H`, `M2(c)`: **the organisation's birth, for the account that is
   * born with it.** `DECISION ADDED 05-15` makes `partnerships.partner_accounts`
   * the row a prospect and a partner share, born when the organisation is
   * opened; until this round identity's door called `partnerships/api` to
   * write it in the same transaction , an edge `04` §4.2.18 does not draw
   * and whose reverse it does, a cycle. The architect's ruling of 13
   * September: identity publishes the birth through the outbox in the door's
   * own transaction, partnerships gives birth to the account by consuming it
   * , transactional by the outbox's guarantee, not by the call.
   * `{ organisationId, name, type, openedByUserId, at }`.
   */
  organisationOpened: "identity.OrganisationOpened",
  /**
   * Round `L-2`, `M1` (`P1-LND-047`): 04 §4.4's row, `{ challengeId, phone,
   * channel, expiresAt }`, published with the channel that ACTUALLY carried the
   * code, and `purpose` since `L-2` (both texts): the transaction code the
   * buyer's acceptance requires names the channel on his screen from this.
   * The login's code does not publish it yet, flagged.
   */
  otpIssued: "identity.OtpIssued",
  /**
   * Round `WF-CON-11-NEW-BANK-PIPELINE`, `M2` (`OD-42`): **a person accepted an invitation and is active** ,
   * published by the acceptance door in its own transaction, once per acceptance, for every organisation
   * type. `{ invitationId, userId, organisationId, role, at }`. `lending` reads it as the second trigger of a
   * new bank's proposals: the bank's FIRST active person is the moment its pipeline can first be read.
   */
  invitationAccepted: "identity.InvitationAccepted",
  /**
   * `OD-37` round 6, workflow v3 Step 0 (ruling Q16): **the borrower edited his own declared profile** ,
   * published by `PUT /api/v1/me/profile` in the write's own transaction, only when a field actually changed.
   * `{ clientId, changes: [{ field, old, new }], at }`; `field` is the stored column or block (`full_name`, `city`,
   * `born_on`, `declared_address`, `employments`, `commitments`, `no_commitments_declared`). `lending` reads it to
   * write the bank's notice « profil modifié depuis la soumission » on every open submission of the borrower;
   * nothing is recomputed from it.
   */
  clientProfileChanged: "identity.ClientProfileChanged",
  /**
   * Round 8, `M1` (the architect's ruling of 16 October 2026; 04 §4.4, WF-CON-40 extended): **the administration
   * deactivated a client account.** Published by `POST /api/v1/clients/{id}/deactivate` in the write's own
   * transaction. `{ clientId, at, by, reason }`. Nothing consumes it: no file moves and nothing is purged.
   */
  clientDeactivated: "identity.ClientDeactivated",
  /** Round 8, `M1`: the pair of the above, `POST /api/v1/clients/{id}/reactivate`. Same payload. */
  clientReactivated: "identity.ClientReactivated",
  /**
   * Partner accounts, `M1` (the architect's order of 16 October 2026; the founder's ruling of that day): **the
   * administration deactivated a partner's account over the head of its own responsable.** Published by
   * `POST /api/v1/accounts/{userId}/deactivate` in the write's own transaction. `notifications` tells the
   * organisation's responsable. The reason is the audit trail's and is never in this payload.
   */
  partnerAccountDeactivated: "identity.PartnerAccountDeactivated",
} as const;

/** `identity.PartnerAccountDeactivated`'s payload. No reason, no email: a notice carries neither. */
export interface PartnerAccountDeactivatedPayload {
  readonly userId: string;
  readonly fullName: string;
  readonly organisationId: string;
  readonly organisationName: string;
  /** ISO date. */
  readonly at: string;
  /** The administration user. */
  readonly by: string;
  /** The responsable who took what the person held (the organisation's own first-account role); null when nothing was held. */
  readonly successorUserId: string | null;
  /** Every live responsable of the organisation, the person excepted: who is told. */
  readonly recipientUserIds: readonly string[];
  readonly reassignedFiles: number;
  readonly reassignedVehicles: number;
}

/** `identity.ClientDeactivated` and `identity.ClientReactivated`'s payload (04 §4.4). */
export interface ClientAccountStatePayload {
  readonly clientId: string;
  /** ISO instant. */
  readonly at: string;
  /** The administration user. */
  readonly by: string;
  /** The required free-text reason. */
  readonly reason: string;
}

/** One changed field of a declared profile, the value before and after (lists as the blocks the client declared). */
export interface ClientProfileChange {
  readonly field: string;
  readonly old: unknown;
  readonly new: unknown;
}

/** `identity.ClientProfileChanged`'s payload. */
export interface ClientProfileChangedPayload {
  readonly clientId: string;
  readonly changes: readonly ClientProfileChange[];
  /** ISO instant. */
  readonly at: string;
}
export const IDENTITY_CLIENT_AGGREGATE = "client";

/** `identity.InvitationAccepted`'s payload. */
export interface InvitationAcceptedPayload {
  readonly invitationId: string;
  readonly userId: string;
  readonly organisationId: string;
  readonly role: string;
  /** ISO instant. */
  readonly at: string;
}

export interface OrganisationOpenedPayload {
  readonly organisationId: string;
  readonly name: string;
  /** `identity.organisation_type`: banque, concessionnaire, cabinet, assureur. */
  readonly type: string;
  readonly openedByUserId: string;
  /** ISO instant. */
  readonly at: string;
}
export const IDENTITY_ORGANISATION_AGGREGATE = "organisation";

export interface StaffAvailabilityChangedPayload {
  readonly userId: string;
  readonly fullName: string;
  readonly state: "actif" | "absent";
  /** ISO date, when absent; null when back. */
  readonly returnDate: string | null;
}

export interface StaffDeactivatedPayload {
  readonly userId: string;
  readonly fullName: string;
  /** ISO date. */
  readonly at: string;
}

export interface StaffInvitedPayload {
  readonly userId: string;
  readonly fullName: string;
  readonly role: string;
  readonly organisationId: string;
}

/**
 * `SENIORITY-LEVEL` (D-1026-AA-1): the analyst's level, a fact about the PERSON set by the chef at the invite (`identity.users.seniority_level`,
 * written once, never updated): the sensitive-stage rule and the equal-charge tie-break of WF-BNK-25 step 4 read it. Canon ranks them junior 0,
 * plain 1, senior 2 (02 §2.4.12); the words are the drawing's (Junior / Standard / Senior).
 */
export const ANALYST_LEVELS = ["junior", "standard", "senior"] as const;
export type AnalystLevel = (typeof ANALYST_LEVELS)[number];

/** 04 §4.4's row: `{ userId, bankOrgId, role: "analysteCredit", invitedBy, level }` (`level` added by record, round `SENIORITY-LEVEL`). */
export interface AnalystInvitedPayload {
  readonly userId: string;
  readonly bankOrgId: string;
  readonly role: "analysteCredit";
  readonly invitedBy: string;
  readonly level: AnalystLevel;
}

/** « Same shape, bank side » as `StaffAvailabilityChangedPayload`, with the bank named. */
export interface AnalystAvailabilityChangedPayload extends StaffAvailabilityChangedPayload {
  readonly bankOrgId: string;
}

/** « Same shape, bank side » as `StaffDeactivatedPayload`, with the bank named. */
export interface AnalystDeactivatedPayload extends StaffDeactivatedPayload {
  readonly bankOrgId: string;
}

export interface InvitationSentPayload {
  readonly invitationId: string;
  readonly userId: string;
  readonly organisationId: string;
  /** Never the link, never the token: an invitation link is a credential (6.4). */
  readonly expiresAt: string;
}

/** `identity.PartnerAdminInvited`, 04 §4.2.1's row: consumed by `partnerships` (journal the onboarding step) and `messaging` (deliver). */
export interface PartnerAdminInvitedPayload {
  readonly invitationId: string;
  readonly orgId: string;
  readonly orgType: string;
  readonly role: string;
  readonly invitedBy: string;
}

/** `identity.OtpIssued`, 04 §4.4's shape with `purpose` beside it (round `L-2`). */
export interface OtpIssuedPayload {
  readonly challengeId: string;
  readonly phone: string;
  readonly channel: "whatsapp" | "sms";
  readonly expiresAt: string;
  readonly purpose: "connexion" | "acceptationOffre" | "consentement";
}

export const IDENTITY_SOURCE_MODULE = "identity";
export const IDENTITY_USER_AGGREGATE = "user";
